import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Loader2, Send, FileText, Calendar, Sparkles, XCircle,
  ArrowRight, User, Phone, Mail
} from "lucide-react";
import { getCategoryPreset } from "@/lib/categoryConfig";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "submitted", label: "Submitted", icon: FileText, copy: "Your preferences are in. Provider is reviewing." },
  { id: "quoted", label: "Quoted", icon: Send, copy: "Provider has sent a quote." },
  { id: "booked", label: "Booked", icon: Calendar, copy: "Locked in. See you soon." },
  { id: "complete", label: "Complete", icon: Sparkles, copy: "Service complete. How was it?" }
];

/**
 * /c/:cardId - 5-stage preference card view (public, read-only by default).
 * Stages: submitted -> quoted -> booked -> complete.  cancelled is terminal and renders a banner.
 * Owner / linked provider / admin can advance via /api/preference-cards/:cardId/stage.
 */
export default function PreferenceCardStagePage() {
  const { cardId } = useParams();
  const t = useAdminCopy();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/preference-cards/${cardId}`);
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, [cardId]);

  useEffect(() => {
    load();
    const token = localStorage.getItem("session_token");
    if (token) {
      fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(u => setMe(u))
        .catch(() => {});
    }
  }, [load]);

  const advance = async (newStage) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/preference-cards/${cardId}/stage`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stage: newStage })
      });
      if (r.ok) {
        toast.success(`Moved to ${newStage}`);
        load();
      } else {
        toast.error("Could not update stage");
      }
    } finally { setUpdating(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-gray-500">Card not found</div>;

  const card = data.card;
  const provider = data.provider;
  const timeline = data.timeline || [];
  const current = data.current_stage;
  const cancelled = current === "cancelled";
  const preset = getCategoryPreset(String(card.category || "").replace(/^cat_/, "").toLowerCase());
  const accentHex = preset?.iconColor || "#9c57f5";
  const lightBg = preset?.bgColor || `${accentHex}1F`;

  const isOwner = me?.user_id && card.user_id === me.user_id;
  const isProvider = me?.user_id && card.provider_user_id === me.user_id;
  const isAdmin = me?.is_admin || me?.is_super_admin;
  const canAdvance = isOwner || isProvider || isAdmin;

  // Determine the next stage to advance to
  const order = ["submitted", "quoted", "booked", "complete"];
  const nextStage = (() => {
    if (cancelled) return null;
    const idx = order.indexOf(current);
    return idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
  })();

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto" data-testid="preference-card-stage-page">
        {/* Header banner */}
        <Card className="overflow-hidden mb-6">
          <div className="h-2" style={{ backgroundColor: accentHex }} />
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: lightBg }}>
                {(() => { const I = preset?.icon || Sparkles; return <I className="w-5 h-5" style={{ color: accentHex }} />; })()}
              </div>
              <div className="flex-1">
                <h1 className="font-bold text-lg" data-testid="card-name">{card.name || "Preference Card"}</h1>
                <p className="text-sm text-gray-500 capitalize">{card.category?.replace(/^cat_/, "")}</p>
              </div>
              {cancelled ? (
                <Badge className="bg-red-50 border-0 text-red-700">Cancelled</Badge>
              ) : (
                <Badge className="bg-gray-100 border-0 text-gray-700 capitalize" data-testid="current-stage-badge">{current}</Badge>
              )}
            </div>
            {provider && (
              <div className="flex items-center gap-2 mt-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">Provider:</span>
                <span className="font-medium">{provider.name}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stage timeline */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="space-y-4">
              {STAGES.map((s, i) => {
                const t = timeline.find(x => x.stage === s.id);
                const state = t?.state || "pending";
                const Icon = s.icon;
                const isActive = state === "active";
                const isComplete = state === "complete";
                return (
                  <div key={s.id} className="flex items-start gap-3" data-testid={`stage-row-${s.id}`}>
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{
                        backgroundColor: isActive ? accentHex : isComplete ? "#10B981" : "#f3f4f6",
                        color: (isActive || isComplete) ? "#fff" : "#9ca3af"
                      }}
                    >
                      {isComplete ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4.5 h-4.5" />}
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium text-sm ${isActive ? "text-gray-900" : isComplete ? "text-gray-700" : "text-gray-400"}`}>
                          {s.label}
                        </span>
                        {isActive && <Badge className="bg-gray-900 text-white text-[10px] border-0">Now</Badge>}
                      </div>
                      <p className={`text-xs ${state === "pending" ? "text-gray-300" : "text-gray-500"}`}>{s.copy}</p>
                    </div>
                    {i < STAGES.length - 1 && (
                      <div className="w-px bg-gray-200 absolute left-[calc(1.5rem+18px)]" style={{ height: 28 }} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Stage-specific client-facing CTAs (owner sees these) */}
        {isOwner && !cancelled && (
          <div className="mb-4 space-y-2" data-testid="stage-ctas">
            {current === "quoted" && card.quoted_price && (
              <Button
                onClick={async () => {
                  const token = localStorage.getItem("session_token");
                  try {
                    const r = await fetch(`${API}/preference-cards/${cardId}/approve-and-pay`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    });
                    const d = await r.json();
                    if (d.checkout_url) window.location.href = d.checkout_url;
                    else if (d.success) { toast.success("Approved - provider notified"); load(); }
                    else toast.error(d.detail || "Could not start checkout");
                  } catch (err) { toast.error(String(err.message || err)); }
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="cta-approve-and-pay"
              >
                {t("confirmation.stage.quoted.cta", "Approve & Pay")}{card.quoted_price ? ` · $${card.quoted_price}` : ""}
              </Button>
            )}
            {current === "booked" && card.appointment?.date && (
              <Button
                variant="outline"
                onClick={() => {
                  const appt = card.appointment || {};
                  const title = encodeURIComponent(appt.service || card.name || "Phygital Appointment");
                  const date = (appt.date || "").replaceAll("-", "");
                  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${date}/${date}`;
                  window.open(url, "_blank");
                }}
                className="w-full"
                data-testid="cta-add-to-calendar"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {t("confirmation.stage.booked.cta", "Add to Calendar")}
              </Button>
            )}
            {current === "complete" && (
              <Button
                onClick={() => {
                  const providerId = card.provider_user_id;
                  window.location.assign(`/review/new?card_id=${cardId}&provider=${providerId || ""}`);
                }}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="cta-leave-review"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t("confirmation.stage.complete.cta", "Leave a Review")}
              </Button>
            )}
          </div>
        )}

        {/* Actions for authorized users */}
        {canAdvance && !cancelled && (
          <div className="flex gap-2">
            {nextStage && (
              <Button
                onClick={() => advance(nextStage)}
                disabled={updating}
                className="flex-1 bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="advance-stage-btn"
              >
                {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Move to {nextStage}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => advance("cancelled")}
              disabled={updating}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="cancel-card-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}

        {/* Provider contact (visible to owner only) */}
        {isOwner && provider && (
          <Card className="mt-4">
            <CardContent className="p-4 flex items-center gap-3">
              {provider.profile_image ? (
                <img src={provider.profile_image} className="w-10 h-10 rounded-full object-cover" alt="" />
              ) : (
                <Circle className="w-10 h-10 text-gray-300" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium">{provider.name}</p>
                <p className="text-xs text-gray-500">Linked provider</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
